import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { PDPHero } from "../apps/configurator/PDPHero";
import { VariantHeroBlock } from "../apps/configurator/VariantHeroBlock";
import { defaults } from "../apps/configurator/defaults";
import { upgradedRootProductSelector } from "../apps/configurator/selectors";
import type { IOptionValues } from "../models/catalogue";
import { isoImageURL, isoProductID } from "../models/nominals";
import { useAppSelector } from "../store/reducers";

type Props = Immutable<templates.PDPHeroTemplate>;
type VariantBlock = NonNullable<
    Props["page"]["variant_hero_images"]
>["value"][number];

export const Template = (props: Props): React.JSX.Element => {
    const selectedRootProduct = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return upgradedRootProductSelector(state);
    });
    const selectedValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const variantBlocks = props.page.variant_hero_images?.value ?? [];
    const winner = pickWinningVariantBlock(
        variantBlocks,
        selectedRootProduct
            ? isoProductID.unwrap(selectedRootProduct.id)
            : null,
        selectedValues,
    );

    if (winner) {
        return <VariantHeroBlock value={winner.value} />;
    }

    const heroImageUrl = props.hero_image_url
        ? isoImageURL.wrap(props.hero_image_url)
        : null;
    const mobileHeroImageUrl = props.mobile_hero_image_url
        ? isoImageURL.wrap(props.mobile_hero_image_url)
        : null;
    return (
        <PDPHero
            defaultBackgroundImage={heroImageUrl}
            defaultMobileBackgroundImage={mobileHeroImageUrl}
            defaultImageTitle={props.hero_image_alt}
        />
    );
};

const isOptionCode = (
    s: string | null,
    values: Immutable<IOptionValues>,
): s is keyof IOptionValues => s !== null && s in values;

export const pickWinningVariantBlock = (
    blocks: readonly VariantBlock[],
    selectedProductId: number | null,
    selectedValues: Immutable<IOptionValues>,
): VariantBlock | null => {
    let best: { score: number; block: VariantBlock } | null = null;
    for (const block of blocks) {
        let score = 0;
        if (
            block.value.product !== null &&
            block.value.product === selectedProductId
        ) {
            score += 2;
        }
        if (block.value.product === null) {
            score += 1;
        }
        if (
            isOptionCode(block.value.attribute, selectedValues) &&
            block.value.value === selectedValues[block.value.attribute]
        ) {
            score += 1;
        }
        if (best === null || score > best.score) {
            best = { score, block };
        }
    }
    return best?.block ?? null;
};
